import React, {useEffect, useMemo, useRef, useState} from 'react'
import {Modal, Collapse} from 'react-bootstrap'
import {KTSVG} from '../../../../../_metronic/helpers'
import {
  createCustomerRateBulkUpload,
  CustomerRateBulkValidationResult,
  downloadCustomerRateBulkTemplate,
  validateCustomerRateBulkUpload,
} from '../core/bulkCustomerRateUpload'
import toaster from '../../../../../Utils/toaster'

type Props = {
  open: boolean
  customerId: string
  onClose: () => void
  onComplete: () => void
}

const isInvalidCell = (
  validation: CustomerRateBulkValidationResult,
  rowNumber: number,
  field: string,
  invalidFields: string[]
) => {
  if (field === 'Row') return !validation.rows.find((r) => r.row === rowNumber)?.isValid
  return invalidFields.some((f) => f.toLowerCase() === field.toLowerCase())
}

export const BulkCustomerRateUploadModal: React.FC<Props> = ({
  open,
  customerId,
  onClose,
  onComplete,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [file, setFile] = useState<File | null>(null)
  const [validation, setValidation] = useState<CustomerRateBulkValidationResult | null>(null)
  const [createdCount, setCreatedCount] = useState<number | null>(null)
  const [showIssues, setShowIssues] = useState(true)
  const [busy, setBusy] = useState(false)
  const [downloadingTemplate, setDownloadingTemplate] = useState(false)

  const gridHeaders = useMemo(() => {
    if (!validation?.headers?.length) return ['Row']
    return validation.headers.includes('Row') ? validation.headers : ['Row', ...validation.headers]
  }, [validation])

  const reset = () => {
    setFile(null)
    setValidation(null)
    setCreatedCount(null)
    setShowIssues(true)
    setBusy(false)
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  useEffect(() => {
    if (open) {
      reset()
    }
  }, [open])

  const openFilePicker = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
      fileInputRef.current.click()
    }
  }

  const close = () => {
    reset()
    onClose()
  }

  const handleDownloadTemplate = async () => {
    if (!customerId) return
    setDownloadingTemplate(true)
    try {
      await downloadCustomerRateBulkTemplate(customerId)
    } catch {
      toaster('error', 'Unable to download template.')
    } finally {
      setDownloadingTemplate(false)
    }
  }

  const handleFileChange = async (selected: File | null) => {
    if (!selected) {
      setFile(null)
      setValidation(null)
      setCreatedCount(null)
      return
    }

    if (!selected.name.toLowerCase().endsWith('.csv')) {
      toaster('error', 'Only CSV files are supported.')
      if (fileInputRef.current) fileInputRef.current.value = ''
      return
    }

    setFile(selected)
    setValidation(null)
    setCreatedCount(null)
    setShowIssues(true)
    setBusy(true)
    try {
      const result = await validateCustomerRateBulkUpload(customerId, selected)
      setValidation(result)
    } catch (error: any) {
      toaster('error', error?.response?.data?.text ?? 'Unable to validate the uploaded file.')
    } finally {
      setBusy(false)
    }
  }

  const handleCreateRates = async () => {
    if (!file || !validation?.isValid || !customerId) return
    setBusy(true)
    try {
      const result = await createCustomerRateBulkUpload(customerId, file)
      if (result.status === 'S') {
        setCreatedCount(result.create?.createdCount ?? 0)
        toaster('success', result.text || 'Rates created successfully.')
        onComplete()
      } else {
        toaster('error', result.text || 'Unable to create rates.')
      }
    } catch (error: any) {
      toaster('error', error?.response?.data?.text ?? 'Unable to create rates.')
    } finally {
      setBusy(false)
    }
  }

  const hasErrors = (validation?.errorCount ?? 0) > 0

  return (
    <Modal show={open} onHide={close} centered size='xl'>
      <Modal.Header closeButton className='py-3'>
        <Modal.Title className='fs-4 fw-semibold'>Bulk Import Customer Rates</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {createdCount != null ? (
          <div className='text-center py-8'>
            <KTSVG
              path='/media/icons/duotune/general/gen043.svg'
              className='svg-icon-success svg-icon-5hx mb-4'
            />
            <h4 className='fw-semibold mb-2'>Upload complete</h4>
            <p className='text-gray-600 mb-0'>
              Successfully created {createdCount} rate{createdCount === 1 ? '' : 's'}.
            </p>
          </div>
        ) : (
          <>
            <div className='d-flex justify-content-between align-items-start gap-3 mb-4'>
              <p className='text-gray-600 mb-0 fs-6'>
                Download the template, fill in rate details for this customer, then upload the completed CSV.
              </p>
              <button
                type='button'
                className='btn btn-sm btn-light-primary flex-shrink-0'
                onClick={handleDownloadTemplate}
                disabled={downloadingTemplate || !customerId}
              >
                <i className='bi bi-download me-2'></i>
                Download template
              </button>
            </div>

            <div className='d-flex justify-content-end mb-3'>
              <button
                type='button'
                className='btn btn-sm btn-primary'
                onClick={openFilePicker}
                disabled={busy || !customerId}
              >
                <i className='bi bi-upload me-2'></i>
                Upload CSV
              </button>
              <input
                ref={fileInputRef}
                type='file'
                accept='.csv,text/csv'
                hidden
                onChange={(e) => handleFileChange(e.target.files?.[0] ?? null)}
              />
            </div>

            {file && (
              <div className='mb-4'>
                <div className='d-flex justify-content-between align-items-center mb-2'>
                  <span className='text-gray-800 fw-semibold'>{file.name}</span>
                  <button
                    type='button'
                    className='btn btn-icon btn-sm btn-light'
                    aria-label='Remove file'
                    onClick={() => handleFileChange(null)}
                    disabled={busy}
                  >
                    <KTSVG path='/media/icons/duotune/arrows/arr061.svg' className='svg-icon-2' />
                  </button>
                </div>

                {!validation && busy && (
                  <div className='text-gray-600 fs-7 mt-2'>Validating file…</div>
                )}

                {hasErrors && (
                  <div className='text-danger fs-7 mt-3'>
                    *Please re-upload the file after below issues are fixed.
                  </div>
                )}
              </div>
            )}

            {validation && (
              <>
                <div
                  className='d-flex align-items-center justify-content-between rounded px-4 py-3 mb-3'
                  style={{backgroundColor: '#fff8dd'}}
                >
                  <div className='d-flex align-items-center gap-4 flex-wrap'>
                    {validation.errorCount > 0 && (
                      <span className='text-danger fw-semibold d-flex align-items-center'>
                        <i className='bi bi-exclamation-circle me-2'></i>
                        {validation.errorCount} error{validation.errorCount === 1 ? '' : 's'}
                      </span>
                    )}
                    <span className='text-success fw-semibold d-flex align-items-center'>
                      <i className='bi bi-check-circle me-2'></i>
                      {validation.validRowCount} valid row{validation.validRowCount === 1 ? '' : 's'}
                    </span>
                  </div>
                  <span className='badge badge-light-dark fs-7'>Validation results</span>
                </div>

                {validation.rows.length > 0 && (
                  <div className='table-responsive border rounded mb-3' style={{maxHeight: '320px'}}>
                    <table className='table table-sm table-row-bordered align-middle mb-0 fs-7'>
                      <thead className='bg-light sticky-top'>
                        <tr>
                          {gridHeaders.map((header) => (
                            <th key={header} className='text-nowrap px-3 py-2'>
                              {header}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {validation.rows.map((row) => (
                          <tr key={row.row}>
                            {gridHeaders.map((header) => {
                              const invalid = isInvalidCell(
                                validation,
                                row.row,
                                header,
                                row.invalidFields
                              )
                              const value =
                                header === 'Row'
                                  ? String(row.row)
                                  : row.data[header] ??
                                    row.data[
                                      Object.keys(row.data).find(
                                        (k) => k.toLowerCase() === header.toLowerCase()
                                      ) ?? ''
                                    ] ??
                                    ''

                              return (
                                <td
                                  key={`${row.row}-${header}`}
                                  className={`text-nowrap px-3 py-2 ${invalid ? 'bg-light-danger text-danger fw-semibold' : ''}`}
                                  title={
                                    invalid
                                      ? validation.issues.find(
                                          (i) => i.row === row.row && i.field === header
                                        )?.message
                                      : undefined
                                  }
                                >
                                  {value || '-'}
                                </td>
                              )
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {validation.issues.length > 0 && (
                  <div className='border rounded'>
                    <button
                      type='button'
                      className='btn btn-link text-decoration-none text-gray-800 fw-semibold w-100 text-start px-4 py-3'
                      onClick={() => setShowIssues((prev) => !prev)}
                    >
                      Show validation issues
                      <i className={`bi ms-2 ${showIssues ? 'bi-chevron-up' : 'bi-chevron-down'}`}></i>
                    </button>
                    <Collapse in={showIssues}>
                      <div className='px-4 pb-4'>
                        {validation.issues.map((issue, index) => (
                          <div
                            key={`${issue.row}-${issue.field}-${issue.errorType}-${index}`}
                            className='text-danger mb-2'
                          >
                            <i className='bi bi-exclamation-circle me-2'></i>
                            Row {issue.row}
                            {issue.field ? ` (${issue.field})` : ''}: {issue.errorType}
                            {issue.message ? ` — ${issue.message}` : ''}
                          </div>
                        ))}
                      </div>
                    </Collapse>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </Modal.Body>

      <Modal.Footer className='py-3'>
        <button type='button' className='btn btn-light btn-sm' onClick={close} disabled={busy}>
          {createdCount != null ? 'Close' : 'Cancel'}
        </button>
        {createdCount == null && (
          <button
            type='button'
            className='btn btn-primary btn-sm'
            onClick={handleCreateRates}
            disabled={busy || !file || !validation?.isValid}
          >
            {busy ? 'Creating…' : 'Create rates'}
          </button>
        )}
      </Modal.Footer>
    </Modal>
  )
}
